import { useState } from "react";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { RequestReviewsSheet } from "./RequestReviewsSheet";
import { ViewReviewSheet } from "./ViewReviewSheet";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { Search, Star, Calendar, Building2, Flag, MessageSquare, Loader2, Eye } from "lucide-react";

interface Review {
  _id: Id<"clientReviews">;
  businessId: Id<"businesses">;
  locationId?: Id<"serviceLocations">;
  rating: number;
  comment: string;
  reviewDate: string;
  reviewerName: string;
  reviewerEmail?: string;
  platform: "direct";
  status: "published" | "pending" | "flagged";
  helpful: number;
  unhelpful: number;
  response?: string;
  orgId: Id<"organizations">;
  createdBy: Id<"users">;
  createdAt: number;
  updatedAt: number;
  // Extended fields from query
  clientName: string;
  clientLogo?: string;
  locationName?: string;
  createdByName?: string;
}

export default function ClientReviews() {
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterRating, setFilterRating] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isRequestReviewsOpen, setIsRequestReviewsOpen] = useState(false);
  const [isViewReviewOpen, setIsViewReviewOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [flaggingReviews, setFlaggingReviews] = useState<Set<string>>(new Set());

  // Mutations
  const updateStatus = useMutation(api.clientReviews.updateStatus);

  // Fetch reviews and stats from Convex (clientReviews is the single source of truth)
  const reviews = useQuery(api.clientReviews.list) || [];
  const reviewStats = useQuery(api.clientReviews.getStats);
  const recentActivity = useQuery(api.clientReviews.getRecentActivity, { limit: 3 });

  const isLoading = reviews === undefined || reviewStats === undefined;

  // Use reviews directly (no more combining with publicSubmissions)
  const allReviews = reviews;

  const filteredReviews = allReviews.filter((review: any) => {
    // Filter by status
    if (filterStatus !== "all" && review.status !== filterStatus) return false;

    // Filter by rating
    if (filterRating !== "all" && review.rating !== parseInt(filterRating)) return false;

    // Filter by search term
    if (
      searchTerm &&
      !review.clientName.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !review.comment.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !review.reviewerName.toLowerCase().includes(searchTerm.toLowerCase())
    )
      return false;

    return true;
  });

  // Use stats from Convex or fallback to 0
  const averageRating = reviewStats?.averageRating || 0;
  const totalReviews = reviewStats?.totalReviews || 0;
  const ratingCounts = reviewStats?.ratingCounts || {};

  // Flag/unflag handler
  const handleFlagReview = async (review: Review) => {
    setFlaggingReviews((prev) => new Set(prev).add(review._id));
    try {
      const newStatus = review.status === "flagged" ? "published" : "flagged";
      await updateStatus({
        id: review._id,
        status: newStatus,
      });
    } catch (error) {
      console.error("Failed to update review status:", error);
    } finally {
      setFlaggingReviews((prev) => {
        const newSet = new Set(prev);
        newSet.delete(review._id);
        return newSet;
      });
    }
  };

  return (
    <DashboardLayout activeTab="clients">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Ratings & Reviews</h1>
            <p className="mt-1 text-gray-500">Monitor and respond to client feedback</p>
          </div>
          <div className="flex gap-3">
            <Button className="gap-2" onClick={() => setIsRequestReviewsOpen(true)}>
              <MessageSquare className="h-4 w-4" /> Request Reviews
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Card className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-medium">Overall Rating</h3>
            </div>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <div className="text-4xl font-bold">{averageRating.toFixed(1)}</div>
                  <div className="flex items-center">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`h-5 w-5 ${star <= Math.round(averageRating) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`}
                      />
                    ))}
                  </div>
                  <div className="text-sm text-gray-500">({totalReviews} reviews)</div>
                </div>
                <div className="mt-4 space-y-2">
                  {[5, 4, 3, 2, 1].map((rating) => (
                    <div key={rating} className="flex items-center gap-2">
                      <div className="w-8 text-sm text-gray-600">{rating} ★</div>
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
                        <div
                          className="h-full rounded-full bg-yellow-400"
                          style={{
                            width:
                              totalReviews > 0
                                ? `${((ratingCounts[rating] || 0) / totalReviews) * 100}%`
                                : "0%",
                          }}
                        ></div>
                      </div>
                      <div className="w-8 text-right text-sm text-gray-600">
                        {ratingCounts[rating] || 0}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Card>

          <Card className="p-6">
            <h3 className="mb-4 text-lg font-medium">Recent Activity</h3>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : recentActivity && recentActivity.length > 0 ? (
              <>
                <div className="space-y-4">
                  {recentActivity.map((review: any) => (
                    <div key={review._id} className="flex items-start gap-3">
                      <div
                        className={`flex-shrink-0 rounded-full p-2 ${
                          review.rating >= 4
                            ? "bg-green-100"
                            : review.rating >= 3
                              ? "bg-yellow-100"
                              : "bg-red-100"
                        }`}
                      >
                        <Star
                          className={`h-4 w-4 ${
                            review.rating >= 4
                              ? "text-green-600"
                              : review.rating >= 3
                                ? "text-yellow-600"
                                : "text-red-600"
                          }`}
                        />
                      </div>
                      <div>
                        <p className="text-sm font-medium">
                          New {review.rating}-star review received
                        </p>
                        <p className="text-xs text-gray-500">{review.clientName}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(review.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <Button variant="outline" className="mt-4 w-full text-sm">
                  View All Activity
                </Button>
              </>
            ) : (
              <div className="py-8 text-center">
                <p className="text-sm text-gray-500">No recent activity</p>
              </div>
            )}
          </Card>
        </div>

        {/* Reviews List */}
        <Card className="p-6">
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
              <Input
                placeholder="Search reviews..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select value={filterStatus} onValueChange={(value) => setFilterStatus(value)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="flagged">Flagged</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterRating} onValueChange={(value) => setFilterRating(value)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by rating" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Ratings</SelectItem>
                <SelectItem value="5">5 Stars</SelectItem>
                <SelectItem value="4">4 Stars</SelectItem>
                <SelectItem value="3">3 Stars</SelectItem>
                <SelectItem value="2">2 Stars</SelectItem>
                <SelectItem value="1">1 Star</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : filteredReviews.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-gray-500">
                  {reviews.length === 0 ? "No reviews yet" : "No reviews match your filters"}
                </p>
              </div>
            ) : (
              filteredReviews.map((review: any) => (
                <div key={review._id} className="space-y-3 rounded-lg border p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                        <Building2 className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-medium">{review.clientName}</h4>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`h-4 w-4 ${star <= review.rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`}
                              />
                            ))}
                          </div>

                          <span>•</span>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {review.reviewDate}
                          </div>
                          {review.locationName && (
                            <>
                              <span>•</span>
                              <span>{review.locationName}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {review.status === "flagged" && (
                        <span className="rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800">
                          Flagged
                        </span>
                      )}
                      {review.status === "pending" && (
                        <span className="rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800">
                          Pending
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedReview(review);
                          setIsViewReviewOpen(true);
                        }}
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        View
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleFlagReview(review)}
                        disabled={flaggingReviews.has(review._id)}
                        className={`${
                          review.status === "flagged"
                            ? "text-green-600 hover:bg-green-50 hover:text-green-700"
                            : "text-red-600 hover:bg-red-50 hover:text-red-700"
                        }`}
                      >
                        {flaggingReviews.has(review._id) ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Flag className="mr-2 h-4 w-4" />
                        )}
                        Flag Review
                      </Button>
                    </div>
                  </div>
                  <p className="text-gray-700">{review.comment}</p>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
      <RequestReviewsSheet
        isOpen={isRequestReviewsOpen}
        onClose={() => setIsRequestReviewsOpen(false)}
      />
      <ViewReviewSheet
        isOpen={isViewReviewOpen}
        onClose={() => setIsViewReviewOpen(false)}
        review={selectedReview}
      />
    </DashboardLayout>
  );
}
